import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { go, replace } from 'react-router-redux';
import { put, select } from 'redux-saga/effects';
import { RootState, store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import instance, { CustomAxiosError } from 'src/axios';
import {
  ApiPaginationResponse,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import { apiRoutes } from 'src/constants/apiConstants';
import { Errors } from 'src/constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeModal,
  setGlobalLoading,
  startLoading,
  stopLoading,
} from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import {
  addProduct,
  editProduct,
  getErrorsTable,
  getProduct,
  getProductList,
  removeProduct,
  setErrorsTable,
  setProduct,
  setProductList,
} from '../actions/productActions';
import { Role } from '../models/Auth';
import { Error } from '../models/Errors';
import { ModalType } from '../models/Modal';
import { Product } from '../models/Product';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';

/* Get product list */
export function* ProductGetListSaga(
  action: ReturnType<typeof getProductList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;

    const paramsString = cleanQueryParams<
      TableQueryParameters & { mId?: string }
    >({
      ...action.payload.params,
      ...(state.auth?.user?.role === Role.SERVICE && {
        mId: action.payload?.mId,
      }),
    });

    const response = (yield instance.get(
      `${apiRoutes.PRODUCTS}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<Product[]>>;

    yield put(setProductList(response?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Add product */
export function* ProductAddProductSaga(
  action: ReturnType<typeof addProduct>
): Generator {
  yield put(startLoading({ actionType: action.type }));
  try {
    (yield instance.post(`${apiRoutes.PRODUCTS}`, {
      ...action.payload,
      imageUrl: action.payload?.imageUrl || null,
    })) as AxiosResponse<ApiResponse<Product>>;

    yield put(
      addModal({
        id: '1',
        title: i18next.t('products:product_added'),
        body: i18next.t('products:product_added_details', {
          model: action.payload?.model,
        }),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('products:add_another_product'),
        secondaryButtonText: i18next.t('products:back_to_products'),
        primaryAction: () => {
          store.dispatch(replace(routes.ADD_PRODUCT, { forcePush: true }));
          store.dispatch(go(0));
          store.dispatch(replace(routes.ADD_PRODUCT, null));
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(removeModal());
          store.dispatch(replace(routes.PRODUCTS, { forcePush: true }));
        },
      })
    );
  } catch (e) {
    yield put(removeModal());
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get product */
export function* ProductGetProductSaga(
  action: ReturnType<typeof getProduct>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.get(
      `${apiRoutes.PRODUCTS}/${action.payload?.productId}`
    )) as AxiosResponse<ApiResponse<Product>>;

    yield put(setProduct(response?.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Edit product */
export function* ProductEditProductSaga(
  action: ReturnType<typeof editProduct>
): Generator {
  yield put(setGlobalLoading(true));
  try {
    const response = (yield instance.put(
      `${apiRoutes.PRODUCTS}/${action.payload?.id}`,
      {
        ...action.payload,
      }
    )) as AxiosResponse<ApiResponse<Product>>;

    yield put(setProduct(response?.data?.data));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('common:changes_saved'),
        body: i18next.t('common:changes_saved_success'),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Remove product */
export function* ProductRemoveProductSaga(
  action: ReturnType<typeof removeProduct>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    (yield instance.delete(
      `${apiRoutes.PRODUCTS}/${action.payload?.productId}`
    )) as AxiosResponse<ApiResponse<Product>>;

    yield put(removeModal());
    yield put(replace(routes.PRODUCTS, { forcePush: true }));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('products:product_removed'),
        body: i18next.t('products:product_removed_success'),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get errors table */
export function* ProductGetErrorsSaga(
  action: ReturnType<typeof getErrorsTable>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const paramsString = cleanQueryParams<TableQueryParameters>(
      action.payload?.params
    );

    const response = (yield instance.get(
      `${apiRoutes.ERROR}${paramsString}`
    )) as AxiosResponse<ApiResponse<Error[]>>;

    yield put(setErrorsTable(response?.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}
